import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPen, faTrash } from "@fortawesome/free-solid-svg-icons";
import Card from "shared/Card";
import { errorMessage } from "shared/api/client";
import { useProductImageUploader } from "BSM/Store/hooks/useProductImageUploader";
import {
  largeImageWarning,
  largeImageWarningForSrc,
} from "../imageSizeWarning";
import CollapseToggle from "../CollapseToggle";
import styles from "./ThumbnailCard.module.css";

/**
 * A single Product Image slot (Thumbnail or one of the three Previews).
 *
 * Controlled by the parent editor via `value` / `onChange`. When a file is
 * picked we show an immediate object-URL preview, upload via the shared
 * adapter (`useProductImageUploader` → `shared/api/media.upload`), and on
 * success bubble up `{ id, url }` so the editor can persist it to
 * `product.images.{slot}`.
 */
export function ThumbnailCard({
  title,
  value,
  onChange,
  imageAlt,
  actionLabel,
  className,
  netsuiteUrl,
  uploader: uploaderProp,
  ...props
}) {
  const fileInputRef = useRef(null);
  const [localPreview, setLocalPreview] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [imgLoadError, setImgLoadError] = useState(false);
  const [pendingWarning, setPendingWarning] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const fallbackUploader = useProductImageUploader();
  const uploader = uploaderProp || fallbackUploader;
  const { upload, isUploading, error } = uploader;

  useEffect(() => {
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [objectUrl]);

  // Reset error state whenever the image src changes.
  const previewSrc = localPreview || value?.url || null;
  const prevPreviewSrcRef = useRef(previewSrc);
  useEffect(() => {
    if (prevPreviewSrcRef.current !== previewSrc) {
      prevPreviewSrcRef.current = previewSrc;
      setImgLoadError(false);
    }
  }, [previewSrc]);

  // When the controlled value changes underneath us (version switch, Undo,
  // autosave-restore), drop any local upload preview so the slot reflects the
  // new value instead of the previously-uploaded image. Skip while an upload is
  // in flight so we don't clobber the in-progress blob/asset preview.
  const prevValueUrlRef = useRef(value?.url || null);
  useEffect(() => {
    const valueUrl = value?.url || null;
    if (prevValueUrlRef.current !== valueUrl) {
      prevValueUrlRef.current = valueUrl;
      if (!isUploading) {
        setLocalPreview(null);
        setObjectUrl(null);
        setPendingWarning(null);
      }
    }
  }, [value, isUploading]);

  const hasImage = Boolean(previewSrc);

  // The NetSuite asset (Store.Assets) is a BASE, not a value. It is displayed
  // when the admin hasn't uploaded anything, but it is deliberately kept out of
  // `value` so that opening and saving a product cannot bake the NetSuite URL in
  // as a fake admin override — the same rule the Pricing and Stock cards follow.
  const displaySrc = previewSrc || netsuiteUrl || null;
  const hasDisplay = Boolean(displaySrc);

  // Persist the advisory across reloads by deriving it from the current image
  // src (a data URL in the mock env). During the brief blob-URL phase before
  // upload resolves, fall back to the size captured from the picked File.
  const sizeWarning = largeImageWarningForSrc(previewSrc) || pendingWarning;

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    uploader.reset?.();
    setPendingWarning(largeImageWarning(file));

    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
    const nextBlobUrl = URL.createObjectURL(file);
    setLocalPreview(nextBlobUrl);
    setObjectUrl(nextBlobUrl);

    try {
      const asset = await upload(file);
      setLocalPreview(asset.url);
      URL.revokeObjectURL(nextBlobUrl);
      setObjectUrl(null);
      uploader.reset?.();
      onChange?.({ id: asset.id, url: asset.url });
    } catch (err) {
      // Revert to the original persisted image so the user sees the upload
      // did not take effect. The error message is shown below.
      setLocalPreview(null);
      URL.revokeObjectURL(nextBlobUrl);
      setObjectUrl(null);
      setPendingWarning(null);
      console.error("Thumbnail upload failed:", err);
    }
  };

  const handleRemove = () => {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }
    setLocalPreview(null);
    setPendingWarning(null);
    onChange?.(null);
  };

  return (
    <Card
      className={classNames(
        styles.card,
        { [styles.cardCollapsed]: collapsed },
        className
      )}
      {...props}
    >
      <div
        className={classNames(styles.header, {
          [styles.headerCollapsed]: collapsed,
        })}
      >
        {title && <p className={styles.title}>{title}</p>}
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => setCollapsed((v) => !v)}
        />
      </div>

      {!collapsed && (
        <>
          <div className={styles.content}>
            <div
              className={classNames(styles.preview, {
                [styles.previewEmpty]: !hasDisplay,
              })}
            >
              {hasDisplay ? (
                imgLoadError ? (
                  <div className={styles.imgError}>
                    <span className={styles.imgErrorIcon}>⚠</span>
                    <span className={styles.imgErrorText}>
                      Image not available
                    </span>
                    <span className={styles.imgErrorHint}>
                      {displaySrc?.split("/").pop() || ""}
                    </span>
                  </div>
                ) : (
                  <img
                    className={styles.image}
                    src={displaySrc}
                    alt={imageAlt}
                    onError={() => setImgLoadError(true)}
                  />
                )
              ) : (
                <span className={styles.placeholder}>No Image</span>
              )}
              {isUploading && (
                <div className={styles.uploadingOverlay} role="status">
                  <span>Uploading…</span>
                </div>
              )}
            </div>

            <div className={styles.actions}>
              {actionLabel && (
                <button
                  type="button"
                  className={styles.actionButton}
                  onClick={handleButtonClick}
                  disabled={isUploading}
                  title={
                    hasDisplay
                      ? "Replace this image with a new one from your computer."
                      : "Upload an image from your computer."
                  }
                >
                  <FontAwesomeIcon icon={faPen} className={styles.actionIcon} />
                  {hasDisplay ? "Change" : actionLabel}
                </button>
              )}
              {hasImage && (
                <button
                  type="button"
                  className={styles.removeButton}
                  onClick={handleRemove}
                  disabled={isUploading}
                  title="Remove this image from the slot."
                >
                  <FontAwesomeIcon
                    icon={faTrash}
                    className={styles.actionIcon}
                  />
                  Remove
                </button>
              )}
            </div>
          </div>

          {error && (
            <p className={styles.error} role="alert">
              {errorMessage(
                error,
                "The image couldn't be uploaded. Try again."
              )}
            </p>
          )}

          {sizeWarning && <p className={styles.sizeWarning}>{sizeWarning}</p>}
        </>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.fileInput}
        onChange={handleFileChange}
      />
    </Card>
  );
}

ThumbnailCard.propTypes = {
  title: PropTypes.string,
  value: PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string,
  }),
  onChange: PropTypes.func,
  imageAlt: PropTypes.string,
  actionLabel: PropTypes.string,
  /**
   * The NetSuite asset URL for this product. Shown as a base image when the
   * admin has not uploaded one. Never written back into `value`.
   */
  netsuiteUrl: PropTypes.string,
  className: PropTypes.string,
  uploader: PropTypes.shape({
    upload: PropTypes.func.isRequired,
    isUploading: PropTypes.bool,
    error: PropTypes.any,
    reset: PropTypes.func,
  }),
};

ThumbnailCard.defaultProps = {
  title: "Thumbnail Image",
  value: null,
  imageAlt: "Product image",
  actionLabel: "Select",
  netsuiteUrl: null,
};
